import { useEffect, useMemo, useRef, useState } from 'react';
import { latestProjects } from '../../data/latestProjects';
import { countLatestByFilter, matchesLatestFilter, type LatestFilter } from '../../utils/latestProjects';

const published = latestProjects.filter((p) => p.publication === 'published');

export function useLatestProjectsDrawer(open: boolean, onClose: () => void) {
  const [filter, setFilter] = useState<LatestFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const savedScroll = useRef(0);
  const returnId = useRef<string | null>(null);

  const counts = useMemo(() => countLatestByFilter(published), []);
  const visible = useMemo(() => published.filter((p) => matchesLatestFilter(p, filter)), [filter]);
  const selected = published.find((p) => p.id === selectedId) ?? null;

  // Focus the close button on open; reset to the list view on close.
  useEffect(() => {
    if (open) {
      const t = window.setTimeout(() => closeRef.current?.focus(), 30);
      return () => window.clearTimeout(t);
    }
    setSelectedId(null);
    returnId.current = null;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  // Move focus and scroll position between the list and detail views.
  useEffect(() => {
    if (!open) return;
    const scroller = scrollRef.current;
    if (selectedId) {
      scroller?.scrollTo(0, 0);
      backRef.current?.focus();
    } else if (returnId.current) {
      if (scroller) scroller.scrollTop = savedScroll.current;
      document.getElementById(`latest-view-${returnId.current}`)?.focus({ preventScroll: true });
    }
  }, [selectedId, open]);

  const viewProject = (id: string) => {
    savedScroll.current = scrollRef.current?.scrollTop ?? 0;
    returnId.current = id;
    setSelectedId(id);
  };

  const backToList = () => setSelectedId(null);

  return { filter, setFilter, counts, visible, selected, viewProject, backToList, scrollRef, closeRef, backRef };
}
